import * as React from 'react'
import { cn } from '@/shared/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import { avatarInitial } from '@/shared/utils/avatar-initial'

type CellUserProps = React.HTMLAttributes<HTMLDivElement> & {
  /** Nombre visible. Si falta, la celda muestra `emptyLabel`. */
  name?: string | null
  /** URL de la foto. Sin ella cae a la inicial del nombre. */
  avatar?: string | null
  /** Qué decir cuando no hay persona. Por defecto, una raya. */
  emptyLabel?: string
  /** Ancho máximo del nombre antes de truncar. */
  nameClassName?: string
}

/**
 * Una PERSONA en una celda: su avatar y su nombre.
 *
 * Headless del dominio —no sabe qué es un asesor, un autor ni un propietario—, así
 * que sirve para cualquier columna que muestre gente. La data se inyecta por props.
 *
 * El `seed` del Avatar es el NOMBRE y no el id: así el color de fondo de la inicial
 * es estable para la misma persona en todas las tablas, que es lo que hace que el
 * ojo la reconozca de una pantalla a otra.
 */
export const CellUser = React.forwardRef<HTMLDivElement, CellUserProps>(
  ({ name, avatar, emptyLabel = '—', className, nameClassName, ...props }, ref) => {
    if (!name) {
      return <span className={cn('text-muted-foreground', className)}>{emptyLabel}</span>
    }

    return (
      <div ref={ref} className={cn('flex items-center gap-2 min-w-0', className)} {...props}>
        <Avatar seed={name} className="h-7 w-7 shrink-0">
          <AvatarImage src={avatar ?? undefined} className="object-cover" />
          <AvatarFallback className="text-[10px] uppercase">
            {avatarInitial({ name })}
          </AvatarFallback>
        </Avatar>
        <span className={cn('truncate', nameClassName)}>{name}</span>
      </div>
    )
  }
)
CellUser.displayName = 'CellUser'
